import { lazy, Suspense, useCallback, type ErrorInfo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { AppBar, Toolbar, Box } from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import { useEntity } from '../../contexts/EntityContext';
import { useLoggerWithContext } from '../../services/logger/loggerContext';
import { TestIds, HEADER_HEIGHT } from '../../constants';
import { HeaderBrand } from './HeaderBrand';
import { UserProfileMenu } from './UserProfileMenu';
import { useFeatureFlag, FeatureFlagKey } from '../../hooks/useFeatureFlags';
import { HeaderActionSlot } from './HeaderActionSlot';

// Lazy-load feature-flagged components to keep their dependencies
// out of the main bundle until the flag is enabled
const OgAssistButton = lazy(() =>
  import('./OgAssistButton').then((m) => ({ default: m.OgAssistButton }))
);
const NotificationBell = lazy(() =>
  import('./Notifications/NotificationBell').then((m) => ({ default: m.NotificationBell }))
);

interface HeaderProps {
  readonly onMenuClick: () => void;
}

export function Header({ onMenuClick }: HeaderProps) {
  const { user, logout } = useAuth();
  const { activeEntity } = useEntity();
  const logger = useLoggerWithContext();
  const ogAssistEnabled = useFeatureFlag(FeatureFlagKey.PSP_UI_OG_ASSIST);
  const notificationsEnabled = useFeatureFlag(FeatureFlagKey.PSP_UI_NOTIFICATIONS);
  const onBoundaryError = useCallback(
    (error: unknown, info: ErrorInfo) => {
      logger.error('Header component failed to load', {
        error: error instanceof Error ? error.message : String(error),
        componentStack: info.componentStack,
      });
    },
    [logger]
  );

  return (
    <AppBar
      data-testid={TestIds.HEADER}
      position="static"
      elevation={0}
      sx={{
        backgroundColor: 'background.paper',
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: 'divider',
        flexShrink: 0,
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ height: HEADER_HEIGHT, minHeight: HEADER_HEIGHT }}>
        {/* Left Section - Logo + Menu Toggle */}
        <HeaderBrand
          onMenuClick={onMenuClick}
          entityDisplayName={activeEntity?.displayName || ''}
        />

        {/* Spacer */}
        <Box flexGrow={1} />

        {/* Right Section - OG Assist + Notifications + User Profile */}
        {ogAssistEnabled && (
          <ErrorBoundary fallback={null} onError={onBoundaryError}>
            <Suspense
              fallback={
                <Box
                  sx={{
                    mr: 1,
                    width: (theme) => theme.spacing(6),
                    height: (theme) => theme.spacing(6),
                    flexShrink: 0,
                  }}
                  aria-hidden
                />
              }
            >
              <OgAssistButton />
            </Suspense>
          </ErrorBoundary>
        )}

        {/* Portal slot — remote apps render directly into this DOM node via createPortal */}
        <HeaderActionSlot />

        {notificationsEnabled && (
          <ErrorBoundary fallback={null} onError={onBoundaryError}>
            <Suspense fallback={null}>
              <NotificationBell />
            </Suspense>
          </ErrorBoundary>
        )}
        <UserProfileMenu user={user} onLogout={logout} />
      </Toolbar>
    </AppBar>
  );
}
